import {
  useGetCurrentUserAnalysisQuotasQuery,
  useGetCurrentUserSubscription,
  useGetCurrentUserVeilleQuotasQuery,
} from '@/shared/hooks/queries/generated';

// ============================================================================
// Usage des quotas — SERVEUR (chantier pricing 2026-08-17). Les quotas
// produit/vendeur sont MENSUELS, ancrés sur le cycle Stripe courant ; pendant
// l'essai gratuit le serveur applique les quotas réduits du plan (8/5/3).
// L'ancien mock localStorage (creditStore) est supprimé.
// ============================================================================

export type CreditKind = 'product' | 'vendor';

export interface CreditUsage {
  /** false tant que les quotas serveur ne sont pas chargés — ne jamais bloquer une action sur un état non-ready. */
  ready: boolean;
  planLabel: string;
  trialing: boolean;
  /** Jours restants d'essai (0 si non-essai). */
  trialDaysLeft: number;
  limits: { product: number; vendor: number; veille: number };
  used: { product: number; vendor: number };
  remaining: { product: number; vendor: number };
  veille: { used: number; max: number };
  canCreate: { product: boolean; vendor: boolean };
}

export const useCredits = (): CreditUsage => {
  const { data: quotasData } = useGetCurrentUserAnalysisQuotasQuery(undefined, {
    staleTime: 30_000,
    retry: 1,
  });
  const { data: veilleData } = useGetCurrentUserVeilleQuotasQuery(undefined, { staleTime: 60_000, retry: 1 });
  const { data: subData } = useGetCurrentUserSubscription({}, { staleTime: 60_000, retry: 1 });

  const quotas = quotasData?.getCurrentUserAnalysisQuotas;
  const veille = veilleData?.getCurrentUserVeilleQuotas;
  const subscription = subData?.getCurrentUserSubscription;

  const trialing = subscription?.isTrialing ?? false;
  // Pendant l'essai, nextRenewalAt = fin d'essai côté Stripe
  const trialDaysLeft =
    trialing && subscription?.plan?.nextRenewalAt
      ? Math.max(0, Math.ceil((Date.parse(subscription.plan.nextRenewalAt) - Date.now()) / 86_400_000))
      : 0;

  return {
    ready: quotas != null,
    planLabel: subscription?.plan?.name ?? '·',
    trialing,
    trialDaysLeft,
    limits: {
      product: quotas?.catalogAnalysesMax ?? 0,
      vendor: quotas?.profileAnalysesMax ?? 0,
      veille: veille?.veillesMax ?? 0,
    },
    used: {
      product: quotas?.catalogAnalysesUsed ?? 0,
      vendor: quotas?.profileAnalysesUsed ?? 0,
    },
    remaining: {
      product: quotas?.catalogAnalysesAvailable ?? 0,
      vendor: quotas?.profileAnalysesAvailable ?? 0,
    },
    veille: { used: veille?.veillesUsed ?? 0, max: veille?.veillesMax ?? 0 },
    // Optimiste tant que non chargé : le serveur reste le juge de paix
    canCreate: {
      product: quotas?.canCreateCatalogAnalysis ?? true,
      vendor: quotas?.canCreateProfileAnalysis ?? true,
    },
  };
};
